import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";

import { brandMarkPath } from "../common/BrandLockup";

type AuthenticatedShellStatus = "idle" | "loading" | "success" | "error";
export type AuthenticatedShellSection =
  | "today"
  | "agenda"
  | "customers"
  | "conversations"
  | "services"
  | "team"
  | "finance"
  | "subscription"
  | "insights"
  | "settings";
type ShellIconName = "home" | "calendar" | "users" | "chat" | "scissors" | "team" | "coin" | "card" | "chart" | "settings" | "link" | "search" | "whatsapp" | "bell" | "user" | "dots" | "logout" | "sidebar";
type ActiveUtilityPanel = "whatsapp" | "notifications" | "link" | "profile" | null;
type MobileNavItem = {
  key: string;
  icon: ShellIconName;
  label: string;
  active: boolean;
  onClick: () => void;
};

type AuthenticatedAppShellProps = {
  title: string;
  status: AuthenticatedShellStatus;
  children: ReactNode;
  activeSection: AuthenticatedShellSection;
  visibleSections?: AuthenticatedShellSection[];
  showSessionForm: boolean;
  sessionToken: string;
  sessionLoading: boolean;
  onSectionChange: (section: AuthenticatedShellSection) => void;
  onSessionTokenChange: (value: string) => void;
  onSessionSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onLogout?: () => void;
  logoutDisabled?: boolean;
  userEmail?: string;
  whatsAppConnected?: boolean;
  bookingLinkHref?: string;
  tenantName?: string;
  bookingSlug?: string;
};

export function AuthenticatedAppShell({
  title,
  status,
  children,
  activeSection,
  visibleSections,
  showSessionForm,
  sessionToken,
  sessionLoading,
  onSectionChange,
  onSessionTokenChange,
  onSessionSubmit,
  onLogout,
  logoutDisabled = false,
  userEmail,
  whatsAppConnected = false,
  bookingLinkHref = "/go/thh-staging",
  tenantName = "THH Staging",
  bookingSlug = "thh-staging"
}: AuthenticatedAppShellProps) {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeUtilityPanel, setActiveUtilityPanel] = useState<ActiveUtilityPanel>(null);
  const allowedSections = visibleSections ?? ["today", "agenda", "customers", "conversations", "services", "team", "finance", "insights", "settings"];
  const canShow = (section: AuthenticatedShellSection) => allowedSections.includes(section);
  const canShowFinance = canShow("finance") || canShow("subscription");
  const commandItems = useMemo(() => [
    { section: "today" as const, label: "Início", hint: "Resumo do dia" },
    { section: "agenda" as const, label: "Agenda", hint: "Ver horários e atendimentos" },
    { section: "customers" as const, label: "Clientes", hint: "Buscar relacionamento" },
    { section: "conversations" as const, label: "Conversas", hint: "Atendimento" },
    { section: "services" as const, label: "Serviços", hint: "Catálogo" },
    { section: "team" as const, label: "Equipe", hint: "Profissionais" },
    { section: (canShow("finance") ? "finance" : "subscription") as AuthenticatedShellSection, label: "Financeiro", hint: "Plano e cobrança" },
    { section: "insights" as const, label: "Insights", hint: "Sinais operacionais" },
    { section: "settings" as const, label: "Ajustes", hint: "Configurações" }
  ].filter((item) => item.label === "Financeiro" ? canShowFinance : canShow(item.section)), [allowedSections, canShowFinance]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandPaletteOpen((current) => !current);
      }

      if (event.key === "Escape") {
        setCommandPaletteOpen(false);
        setActiveUtilityPanel(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  function handleCommand(section: AuthenticatedShellSection) {
    setCommandPaletteOpen(false);
    onSectionChange(section);
  }

  function handleSearchIntent() {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches) {
      setCommandPaletteOpen(false);
      return;
    }

    setCommandPaletteOpen(true);
  }

  function openUtilityPanel(panel: Exclude<ActiveUtilityPanel, null>) {
    setCommandPaletteOpen(false);
    setActiveUtilityPanel((current) => current === panel ? null : panel);
  }

  function navigate(section: AuthenticatedShellSection) {
    setActiveUtilityPanel(null);
    onSectionChange(section);
  }

  const mobileNavItems: MobileNavItem[] = [
    canShow("today") ? { key: "today", icon: "home", label: "Início", active: activeSection === "today", onClick: () => navigate("today") } : null,
    canShow("agenda") ? { key: "agenda", icon: "calendar", label: "Agenda", active: activeSection === "agenda", onClick: () => navigate("agenda") } : null,
    canShow("customers") ? { key: "customers", icon: "users", label: "Clientes", active: activeSection === "customers", onClick: () => navigate("customers") } : null,
    canShow("conversations") ? { key: "conversations", icon: "chat", label: "Conversas", active: activeSection === "conversations", onClick: () => navigate("conversations") } : null,
    {
      key: "more",
      icon: "dots",
      label: "Mais",
      active: activeUtilityPanel === "profile" || ["services", "team", "finance", "subscription", "insights", "settings"].includes(activeSection),
      onClick: () => openUtilityPanel("profile")
    }
  ].filter((item): item is MobileNavItem => Boolean(item)).slice(0, 5);

  return (
    <section className={`authshell ${sidebarCollapsed ? "authshell--sidebarCollapsed" : ""} ${activeUtilityPanel ? "authshell--drawerOpen" : ""}`}>
      <aside className="authshell__sidebar" aria-label="Navegacao autenticada">
        <a className="authshell__brand" href="/go" aria-label="THH GO">
          <img className="authshell__brandMark" src={brandMarkPath} alt="" aria-hidden="true" />
          <span>THH GO</span>
        </a>
        <nav>
          <div className="authshell__navgroup authshell__navgroup--primary" aria-label="Menu principal">
            {canShow("today") && <NavButton icon="home" active={activeSection === "today"} variant="primary" onClick={() => onSectionChange("today")} label="Início" />}
            {canShow("agenda") && <NavButton icon="calendar" active={activeSection === "agenda"} variant="primary" onClick={() => onSectionChange("agenda")} label="Agenda" />}
            {canShow("customers") && <NavButton icon="users" active={activeSection === "customers"} onClick={() => onSectionChange("customers")} label="Clientes" />}
            {canShow("conversations") && <NavButton icon="chat" active={activeSection === "conversations"} onClick={() => onSectionChange("conversations")} label="Conversas" />}
            {canShow("services") && <NavButton icon="scissors" active={activeSection === "services"} onClick={() => onSectionChange("services")} label="Serviços" />}
            {canShow("team") && <NavButton icon="team" active={activeSection === "team"} onClick={() => onSectionChange("team")} label="Equipe" />}
            {canShowFinance && <NavButton icon="coin" active={activeSection === "finance" || activeSection === "subscription"} onClick={() => onSectionChange(canShow("finance") ? "finance" : "subscription")} label="Financeiro" />}
            {canShow("insights") && <NavButton icon="chart" active={activeSection === "insights"} onClick={() => onSectionChange("insights")} label="Insights" />}
          </div>
          {canShow("settings") && (
            <div className="authshell__navgroup authshell__navgroup--utility" aria-label="Ajustes">
              <NavButton icon="settings" active={activeSection === "settings"} onClick={() => onSectionChange("settings")} label="Ajustes" />
            </div>
          )}
        </nav>
        {onLogout && status === "success" && (
          <button className="authshell__sidebarLogout" type="button" onClick={onLogout} disabled={logoutDisabled}>
            {logoutDisabled ? "Saindo" : "Sair"}
          </button>
        )}
        </aside>

      <main className="authshell__main" aria-label={title}>
        <header className="authshell__topbar">
          <button className="authshell__sidebarToggle" type="button" aria-label={sidebarCollapsed ? "Expandir menu lateral" : "Retrair menu lateral"} onClick={() => setSidebarCollapsed((current) => !current)}>
            <ShellIcon name="sidebar" />
          </button>
          <div className="authshell__mobileBrand" aria-label="THH GO">
            <img className="authshell__mobileBrandMark" src={brandMarkPath} alt="" aria-hidden="true" />
            <strong>THH GO</strong>
            <div>
              <button type="button" aria-label="Notificações" onClick={() => openUtilityPanel("notifications")}>
                <ShellIcon name="bell" />
              </button>
              <button type="button" aria-label="Perfil e conta" onClick={() => openUtilityPanel("profile")}>
                <ShellIcon name="user" />
              </button>
            </div>
          </div>
          <div className="authshell__topbarActions">
            <label className="authshell__search">
              <ShellIcon name="search" />
              <input
                type="search"
                placeholder="Busca rápida..."
                aria-label="Busca rápida"
                onFocus={handleSearchIntent}
                onClick={handleSearchIntent}
                readOnly
              />
              <kbd>⌘ K</kbd>
            </label>
            {commandPaletteOpen && (
              <div className="authshell__commandPalette" role="menu" aria-label="Command palette">
                {commandItems.map((item) => (
                  <button type="button" role="menuitem" key={`${item.section}-${item.label}`} onClick={() => handleCommand(item.section)}>
                    <span>{item.label}</span>
                    <small>{item.hint}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="authshell__topbarMeta">
            <button className="authshell__linkPill" type="button" onClick={() => openUtilityPanel("link")}>
              <ShellIcon name="link" />
              <span>Meu Link</span>
            </button>
            <button
              className={`authshell__iconPill authshell__iconPill--whatsapp ${whatsAppConnected ? "is-connected" : "is-disconnected"}`}
              type="button"
              aria-label={whatsAppConnected ? "WhatsApp conectado" : "WhatsApp desconectado"}
              title={whatsAppConnected ? "WhatsApp conectado" : "WhatsApp desconectado. Clique para iniciar a conexão."}
              onClick={() => openUtilityPanel("whatsapp")}
            >
              <ShellIcon name="whatsapp" />
            </button>
            <button className="authshell__iconPill" type="button" aria-label="Notificações" onClick={() => openUtilityPanel("notifications")}>
              <ShellIcon name="bell" />
            </button>
            <button className="authshell__avatar" type="button" title={userEmail} aria-label="Perfil e conta" onClick={() => openUtilityPanel("profile")}>
              <ShellIcon name="user" />
            </button>
          </div>
        </header>

        {showSessionForm && (
          <form className="authshell__form" onSubmit={onSessionSubmit}>
            <label htmlFor="app-shell-token">Sessão</label>
            <div className="authshell__controls">
              <input
                id="app-shell-token"
                name="token"
                type="password"
                autoComplete="off"
                value={sessionToken}
                onChange={(event) => onSessionTokenChange(event.target.value)}
              />
              <button type="submit" disabled={sessionLoading}>
                {sessionLoading ? "Carregando" : "Carregar"}
              </button>
            </div>
          </form>
        )}

        {children}
      </main>
      {activeUtilityPanel && (
        <UtilityDrawer
          panel={activeUtilityPanel}
          tenantName={tenantName}
          bookingSlug={bookingSlug}
          bookingLinkHref={bookingLinkHref}
          userEmail={userEmail}
          whatsAppConnected={whatsAppConnected}
          canShow={canShow}
          canShowFinance={canShowFinance}
          activeSection={activeSection}
          onNavigate={navigate}
          onLogout={onLogout}
          logoutDisabled={logoutDisabled}
          onClose={() => setActiveUtilityPanel(null)}
        />
      )}
      <nav className="authshell__bottomNav" aria-label="Navegacao principal mobile">
        {mobileNavItems.map((item) => (
          <button
            type="button"
            className={item.active ? "is-active" : ""}
            key={item.key}
            onClick={item.onClick}
          >
            <ShellIcon name={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </section>
  );
}

function UtilityDrawer({
  panel,
  tenantName,
  bookingSlug,
  bookingLinkHref,
  userEmail,
  whatsAppConnected,
  canShow,
  canShowFinance,
  activeSection,
  onNavigate,
  onLogout,
  logoutDisabled,
  onClose
}: {
  panel: Exclude<ActiveUtilityPanel, null>;
  tenantName: string;
  bookingSlug: string;
  bookingLinkHref: string;
  userEmail?: string;
  whatsAppConnected: boolean;
  canShow: (section: AuthenticatedShellSection) => boolean;
  canShowFinance: boolean;
  activeSection: AuthenticatedShellSection;
  onNavigate: (section: AuthenticatedShellSection) => void;
  onLogout?: () => void;
  logoutDisabled: boolean;
  onClose: () => void;
}) {
  const title = {
    whatsapp: "WhatsApp",
    notifications: "Notificações",
    link: "Link de Agendamento",
    profile: "Perfil e conta"
  }[panel];

  return (
    <aside className="authshell__utilityDrawer" aria-label={title}>
      <header>
        <h2>{title}</h2>
        <button type="button" aria-label="Fechar painel" onClick={onClose}>×</button>
      </header>
      {panel === "whatsapp" && <WhatsAppDrawer connected={whatsAppConnected} />}
      {panel === "notifications" && <NotificationsDrawer />}
      {panel === "link" && <BookingLinkDrawer tenantName={tenantName} bookingSlug={bookingSlug} bookingLinkHref={bookingLinkHref} />}
      {panel === "profile" && (
        <ProfileDrawer
          tenantName={tenantName}
          userEmail={userEmail}
          canShow={canShow}
          canShowFinance={canShowFinance}
          activeSection={activeSection}
          onNavigate={onNavigate}
          onLogout={onLogout}
          logoutDisabled={logoutDisabled}
        />
      )}
    </aside>
  );
}

function WhatsAppDrawer({ connected }: { connected: boolean }) {
  return (
    <div className="authshell__drawerBody">
      <span className="authshell__drawerIcon"><ShellIcon name="whatsapp" /></span>
      <h3>{connected ? "WhatsApp conectado" : "Conecte seu WhatsApp"}</h3>
      <p>{connected ? "Confirmações e lembretes estão prontos para este negócio." : "Para confirmações automáticas, lembretes e campanhas. Recomendamos um número exclusivo para o negócio."}</p>
      <button type="button" className="authshell__drawerPrimary">{connected ? "Ver conexão" : "Conectar agora"}</button>
    </div>
  );
}

function NotificationsDrawer() {
  const notifications = [
    ["Agenda", "2 horários livres hoje à tarde."],
    ["Cliente", "Maria Santos confirmou o atendimento."],
    ["Sistema", "WhatsApp ainda não está conectado."]
  ];

  return (
    <div className="authshell__drawerBody authshell__drawerBody--list">
      {notifications.map(([label, message]) => (
        <article className="authshell__notificationItem" key={`${label}-${message}`}>
          <strong>{label}</strong>
          <span>{message}</span>
        </article>
      ))}
    </div>
  );
}

function BookingLinkDrawer({ tenantName, bookingSlug, bookingLinkHref }: { tenantName: string; bookingSlug: string; bookingLinkHref: string }) {
  const fullLink = `https://thh.go/${bookingSlug}`;

  return (
    <div className="authshell__drawerBody">
      <span className="authshell__drawerIcon authshell__drawerIcon--blue"><ShellIcon name="link" /></span>
      <h3>Configure seu link</h3>
      <p>Este é o endereço que seus clientes usarão para agendar horários com você.</p>
      <label className="authshell__linkField">
        <span>Nome do estabelecimento</span>
        <input value={tenantName} readOnly />
      </label>
      <label className="authshell__slugField">
        <span>Endereço do seu aplicativo</span>
        <div><small>thh.go/</small><input value={bookingSlug} readOnly /></div>
      </label>
      <div className="authshell__linkPreview">
        <span>Preview do seu link</span>
        <div><strong>{fullLink}</strong><button type="button">Copiar</button></div>
      </div>
      <a className="authshell__drawerSecondary" href={bookingLinkHref} target="_blank" rel="noreferrer">Acessar página como cliente</a>
      <button type="button" className="authshell__drawerPrimary authshell__drawerPrimary--blue">Salvar configurações</button>
      <small className="authshell__drawerNote">Se o nome já estiver em uso, o sistema pedirá uma correção antes de publicar.</small>
    </div>
  );
}

function ProfileDrawer({
  tenantName,
  userEmail,
  canShow,
  canShowFinance,
  activeSection,
  onNavigate,
  onLogout,
  logoutDisabled
}: {
  tenantName: string;
  userEmail?: string;
  canShow: (section: AuthenticatedShellSection) => boolean;
  canShowFinance: boolean;
  activeSection: AuthenticatedShellSection;
  onNavigate: (section: AuthenticatedShellSection) => void;
  onLogout?: () => void;
  logoutDisabled: boolean;
}) {
  const secondaryItemCandidates: Array<{ section: AuthenticatedShellSection; icon: ShellIconName; label: string }> = [
    { section: "services", icon: "scissors", label: "Serviços" },
    { section: "team", icon: "team", label: "Equipe" },
    { section: canShow("finance") ? "finance" : "subscription", icon: "coin", label: "Plano e contrato" },
    { section: "insights", icon: "chart", label: "Insights" },
    { section: "settings", icon: "settings", label: "Ajustes" }
  ];
  const secondaryItems = secondaryItemCandidates.filter((item) => item.section === "finance" || item.section === "subscription" ? canShowFinance : canShow(item.section));

  return (
    <div className="authshell__drawerBody authshell__drawerBody--list">
      <section className="authshell__profileSummary">
        <span><ShellIcon name="user" /></span>
        <div>
          <strong>{tenantName}</strong>
          <small>{userEmail ?? "Usuário autenticado"}</small>
        </div>
      </section>
      <div className="authshell__moreMenu" aria-label="Mais opcoes">
        {secondaryItems.map((item) => (
          <button
            type="button"
            className={activeSection === item.section ? "is-active" : ""}
            key={item.section}
            onClick={() => onNavigate(item.section)}
          >
            <ShellIcon name={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
        {onLogout && (
          <button type="button" className="authshell__moreLogout" onClick={onLogout} disabled={logoutDisabled}>
            <ShellIcon name="logout" />
            <span>{logoutDisabled ? "Saindo" : "Sair"}</span>
          </button>
        )}
      </div>
    </div>
  );
}

function NavButton({
  active,
  variant = "secondary",
  icon,
  label,
  onClick,
  disabled = false
}: {
  active: boolean;
  variant?: "primary" | "secondary";
  icon: ShellIconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`authshell__navitem authshell__navbutton authshell__navitem--${variant} ${active ? "authshell__navitem--active" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      <i aria-hidden="true"><ShellIcon name={icon} /></i>
      <span>
        <b>{label}</b>
      </span>
    </button>
  );
}

function ShellIcon({ name }: { name: ShellIconName }) {
  const paths: Record<ShellIconName, ReactNode> = {
    home: <><path d="M4 10.5 12 4l8 6.5" /><path d="M6.5 9.5V20h11V9.5" /><path d="M10 20v-5h4v5" /></>,
    calendar: <><path d="M7 4v3" /><path d="M17 4v3" /><path d="M5 8h14" /><rect x="5" y="6" width="14" height="14" rx="3" /><path d="M8 12h3" /><path d="M13 12h3" /><path d="M8 16h3" /></>,
    users: <><path d="M8.5 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" /><path d="M15.5 10a2.5 2.5 0 1 0 0-5" /><path d="M3.5 19a5 5 0 0 1 10 0" /><path d="M14 15.5a4.5 4.5 0 0 1 6 3.5" /></>,
    chat: <><path d="M5 6.5h14v10H8l-3 3v-13Z" /></>,
    scissors: <><path d="m4 19 16-14" /><path d="M7 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" /><path d="M7 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" /></>,
    team: <><rect x="4" y="5" width="16" height="14" rx="3" /><path d="M8 9h8" /><path d="M8 13h3" /><path d="M13 13h3" /><path d="M8 17h8" /></>,
    coin: <><circle cx="12" cy="12" r="8" /><path d="M12 8v8" /><path d="M9.5 10h3.5a2 2 0 0 1 0 4H10" /></>,
    card: <><rect x="4" y="6" width="16" height="12" rx="3" /><path d="M4 10h16" /><path d="M8 15h3" /></>,
    chart: <><path d="M5 19V5" /><path d="M5 19h14" /><path d="M9 16v-5" /><path d="M13 16V8" /><path d="M17 16v-3" /></>,
    settings: <><path d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" /><path d="M18.4 13.5a7 7 0 0 0 0-3l2-1.4-2-3.4-2.4 1a7.6 7.6 0 0 0-2.6-1.5L13 2.6h-4l-.4 2.6A7.6 7.6 0 0 0 6 6.7l-2.4-1-2 3.4 2 1.4a7 7 0 0 0 0 3l-2 1.4 2 3.4 2.4-1a7.6 7.6 0 0 0 2.6 1.5l.4 2.6h4l.4-2.6a7.6 7.6 0 0 0 2.6-1.5l2.4 1 2-3.4-2-1.4Z" /></>,
    link: <><path d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" /><path d="M14 11a5 5 0 0 0-7.1 0l-2 2A5 5 0 0 0 12 20.1l1.1-1.1" /></>,
    search: <><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4" /></>,
    whatsapp: <><path d="M5 19.5 6.2 16A7 7 0 1 1 9 18.1L5 19.5Z" /><path d="M9.4 8.7c.3 2.7 2.2 4.6 4.9 4.9l1-1.1" /><path d="m8.5 8.1 1.1-1" /></>,
    bell: <><path d="M18 9a6 6 0 1 0-12 0c0 7-2 7-2 7h16s-2 0-2-7" /><path d="M10 20a2 2 0 0 0 4 0" /></>,
    user: <><path d="M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" /><path d="M6 20a6 6 0 0 1 12 0" /></>,
    dots: <><path d="M5 12h.01" /><path d="M12 12h.01" /><path d="M19 12h.01" /></>,
    logout: <><path d="M10 6H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4" /><path d="M14 16l4-4-4-4" /><path d="M18 12H9" /></>,
    sidebar: <><rect x="5" y="5" width="14" height="14" rx="2" /><path d="M10 5v14" /></>
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      {paths[name]}
    </svg>
  );
}
